import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, extractApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // If the user is already signed in, kick them to the dashboard. No need to
  // hammer a stale email-token while their session is perfectly valid.
  useEffect(() => {
    if (!authLoading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [authLoading, user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy || done) return;
    setBusy(true); setErr("");
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 1500);
    } catch (e2) {
      const msg = extractApiError(e2);
      // Treat "already been used" as success — the password DID get updated on
      // the prior submit and the user is just seeing the duplicate response.
      if (/already been used/i.test(msg)) {
        setDone(true);
        setTimeout(() => navigate("/login"), 1500);
        return;
      }
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  if (authLoading) return <div className="min-h-screen grid place-items-center"><div className="label-mono animate-pulse">CHECKING SESSION...</div></div>;

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center p-8">
        <div className="text-center">
          <h1 className="font-display text-3xl font-black mb-2">INVALID LINK</h1>
          <p className="text-muted-foreground mb-4">The reset link is missing a token.</p>
          <Link to="/forgot-password" className="text-volt hover:underline">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center p-8">
      <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="reset-form">
        <h1 className="font-display text-4xl font-black mb-2">SET NEW PASSWORD</h1>
        <p className="text-muted-foreground mb-8">Choose a strong new password (min 6 chars).</p>
        <label className="label-mono block mb-2">New Password</label>
        <input value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} type="password"
               className="w-full bg-secondary border border-border px-4 py-3 mb-6 focus:outline-none focus:border-volt"
               data-testid="reset-password-input" />
        {done && <div className="border border-volt text-volt px-4 py-2 mb-4 text-sm" data-testid="reset-success">Password updated. Redirecting to login...</div>}
        {err && <div className="border border-destructive text-destructive px-4 py-2 mb-4 text-sm" data-testid="reset-error">{err}</div>}
        <button disabled={busy || done} className="w-full bg-volt text-black py-4 font-display uppercase tracking-widest font-bold hover:bg-white disabled:opacity-50" data-testid="reset-submit-btn">
          {busy ? "Updating..." : "Update Password"}
        </button>
      </form>
    </div>
  );
}
